"use client";

import { Compass, Pencil, Leaf, Hammer, Sprout } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  number: "01",
  title: "Consultation & Site Assessment",
  description: "Every build begins with a deep understanding of your vision. We evaluate your property, analyze structural requirements, and listen to your goals to ensure our blueprint aligns perfectly with your expectations.",
  tags: ["Site Survey", "Structural Analysis", "Goal Setting", "Budget Planning"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/construction_assessment.png",
  alt: "Americo Moscariello reviewing construction site plans with a client",
  icon: Compass
}, {
  number: "02",
  title: "Architectural Design",
  description: "We translate your requirements into precise technical drawings. Our design process balances aesthetic elegance with structural integrity, ensuring every detail is accounted for before we break ground.",
  tags: ["Blueprints", "3D Modeling", "Material Specs", "Permit Planning"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/construction_design.webp",
  alt: "Detailed architectural blueprints laid out on a drafting table",
  icon: Pencil
}, {
  number: "03",
  title: "Material Procurement",
  description: "Quality is the foundation of our work. We source premium-grade materials, from structural steel to custom finishes, ensuring that every component meets our rigorous standards for durability and performance.",
  tags: ["Premium Sourcing", "Quality Control", "Vendor Coordination", "Logistics"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/construction_materials.png",
  alt: "Selection of high-quality construction materials and stone samples",
  icon: Leaf
}, {
  number: "04",
  title: "Precision Construction",
  description: "Our team executes the build with meticulous attention to detail. From foundation to final trim, we manage every trade on-site to ensure the project is completed safely, on time, and to the highest standard.",
  tags: ["Site Management", "Structural Framing", "Trade Coordination", "Safety Compliance"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/construction_build.png",
  alt: "Construction crew working on a residential framing project",
  icon: Hammer
}, {
  number: "05",
  title: "Property Stewardship",
  description: "Our commitment extends beyond the final inspection. We provide ongoing property management and maintenance to ensure your investment remains in pristine condition for years to come.",
  tags: ["Preventative Care", "Routine Inspections", "System Maintenance", "Client Support"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/construction_stewardship.png",
  alt: "Finished luxury property exterior maintained by Moscariello Construction",
  icon: Sprout
}];
function ProcessStep({
  step,
  index
}: {
  step: (typeof steps)[number];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const isReversed = index % 2 === 1;
  const Icon = step.icon;
  return <div ref={ref} data-index={index} className={`relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      {}
      <div className="hidden lg:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10" aria-hidden="true">
        <div className="size-3 rounded-lg bg-[var(--secondary)] ring-8 ring-background" />
      </div>

      {}
      <div className={`lg:col-span-5 ${isReversed ? "lg:col-start-8" : "lg:col-start-1"}`}>
        <div className="relative overflow-hidden rounded-lg shadow-lg">
          <img src={step.image} alt={step.alt} loading="lazy" decoding="async" className="w-full h-[420px] object-cover transition-transform duration-700 hover:scale-105" />
          <div className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-lg bg-background/90 px-3 py-1.5 backdrop-blur-sm">
            <Icon className="size-3.5 text-[var(--secondary)]" />
            <span className="font-mono text-[11px] uppercase tracking-widest text-foreground">
              Phase {step.number}
            </span>
          </div>
        </div>
      </div>

      {}
      <div className={`lg:col-span-6 relative ${isReversed ? "lg:col-start-1 lg:row-start-1" : "lg:col-start-7"}`}>
        {}
        <div className="pointer-events-none select-none absolute -top-10 -left-2 lg:-left-4 font-mono font-light leading-none" style={{
        fontSize: "120px",
        color: "var(--secondary)",
        opacity: 0.18
      }} aria-hidden="true">
          {step.number}
        </div>

        <div className="relative">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--secondary)] mb-4">
            Phase {step.number}
          </p>
          <h3 className="font-serif font-default text-3xl md:text-4xl lg:text-5xl font-light text-foreground tracking-tight mb-5">
            {step.title.split(" ").map((word, i) => <span key={i}>
                {i === 1 || i === 2 ? <em className="italic">{word}</em> : word}{" "}
              </span>)}
          </h3>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-7 max-w-xl">
            {step.description}
          </p>
          <div className="flex flex-wrap gap-2">
            {step.tags.map((tag, i) => <Badge key={i} data-index={i} variant="outline" className="border-[var(--secondary)]/40 text-foreground bg-transparent rounded-lg px-3 py-1 text-xs font-normal hover:bg-[var(--secondary)]/10 transition-colors">
                {tag}
              </Badge>)}
          </div>
        </div>
      </div>
    </div>;
}
export default function HowWeManage() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="277" id="how-we-manage" className="relative dark bg-background text-foreground py-24 md:py-32 overflow-hidden">
      <div className="container mx-auto max-w-7xl px-6 lg:px-8">
        {}
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-20 md:mb-28 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--secondary)] mb-6">
            Our Process
          </p>
          <h2 className="font-serif font-default text-4xl md:text-5xl lg:text-6xl font-light text-foreground tracking-tight leading-[1.1] mb-6">
            From <em className="italic font-normal">blueprint</em> to{" "}
            <em className="italic font-normal">legacy</em>
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-[600px] mx-auto">
            Moscariello Construction Co, Inc operates through a disciplined five-phase framework, 
            ensuring every project we undertake is managed with the precision and integrity 
            that defines our reputation in Collegeville.
          </p>
        </div>

        {}
        <div className="relative">
          {}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2 bg-gradient-to-b from-transparent via-[var(--secondary)]/40 to-transparent" aria-hidden="true" />

          <div className="space-y-24 md:space-y-32">
            {steps.map((step, i) => <ProcessStep key={i} step={step} index={i} />)}
          </div>
        </div>
      </div>
    </section>;
}